/**
 * AR Aging Report — /finance/reports/ar-aging
 *
 * Full breakdown of outstanding receivables grouped by aging bucket.
 * Sortable by customer, bucket amount, or total.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '@/lib/api';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  RefreshCw, Search, Download, AlertCircle, CreditCard, ArrowRight, BrainCircuit, Clock3,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/lib/toast';
import { FinanceWorkspaceShell } from '../FinanceWorkspaceShell';
import { buildSoaRoute, downloadAuthenticatedCsv, fmtNumber, hasResolvableSoaPartyRef, todayIso } from './report-utils';

// ── Types ─────────────────────────────────────────────────────────────────────

interface ArAgingRow {
  customer_id: string;
  customer_name: string;
  currency: string;
  current: number;
  days_1_30: number;
  days_31_60: number;
  days_61_90: number;
  days_over_90: number;
  total: number;
  last_payment_date?: string;
}

interface ArAgingSummary {
  as_of_date: string;
  currency: string;
  current_total: number;
  days_1_30_total: number;
  days_31_60_total: number;
  days_61_90_total: number;
  days_over_90_total: number;
  grand_total: number;
  rows: ArAgingRow[];
  dso?: number;
  totalInvoiceCount?: number;
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function ArAgingPage() {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [data, setData] = useState<ArAgingSummary | null>(null);
  const [search, setSearch] = useState('');
  const [asOf, setAsOf] = useState(todayIso());
  const [currencyView, setCurrencyView] = useState<'functional' | 'presentation'>('functional');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        asOf,
        currency_view: currencyView,
      });
      if (search.trim()) params.set('search', search.trim());
      const res = await api.get<ArAgingSummary>(`/finance/reports/ar-aging?${params}`);
      setData(res);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [asOf, currencyView, search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({
        asOf,
        currency_view: currencyView,
        format: 'csv',
      });
      if (search.trim()) params.set('search', search.trim());
      await downloadAuthenticatedCsv(`/finance/reports/ar-aging?${params}`, `ar-aging-${asOf}.csv`);
      toast.success('AR Aging CSV 已下载');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '导出失败');
    } finally {
      setExporting(false);
    }
  }, [asOf, currencyView, search, toast]);

  const rows = data?.rows ?? [];
  const topRiskRows = useMemo(
    () => [...rows].sort((a, b) => b.days_over_90 - a.days_over_90).filter((row) => row.days_over_90 > 0).slice(0, 5),
    [rows],
  );
  const topExposure = useMemo(
    () => [...rows].sort((a, b) => b.total - a.total).slice(0, 1)[0] ?? null,
    [rows],
  );
  const statementFrom = `${asOf.slice(0, 4)}-01-01`;

  const buckets = [
    { key: 'current',       label: 'Current',   color: 'text-green-700' },
    { key: 'days_1_30',     label: '1–30 days',  color: 'text-yellow-700' },
    { key: 'days_31_60',    label: '31–60 days', color: 'text-orange-600' },
    { key: 'days_61_90',    label: '61–90 days', color: 'text-red-600' },
    { key: 'days_over_90',  label: '90+ days',   color: 'text-red-800 font-bold' },
  ];

  return (
    <FinanceWorkspaceShell
      title="AR Aging"
      subtitle="Open receivable exposure grouped by aging bucket"
      Icon={CreditCard}
      iconClassName="text-blue-600"
      moduleBadge="Report 5.1"
      domainBadge="Working capital"
      agentTitle="Collections Risk Report"
      agentSummary="This report turns AR aging into an AI-native collections surface: as-of exposure, risk concentration, DSO signal, and direct next actions into collections and statements."
      checklistTitle="AR aging readiness"
      checklistStatus="As-of report"
      checklistNote="The page is now testable as a real as-of aging report with working CSV export, search, currency view, and risk concentration surfaces."
      checklistItems={[
        'Aging reacts to the selected as-of date instead of always showing today.',
        'CSV export uses authenticated download instead of a dead button.',
        'Riskiest overdue customers are surfaced separately for collection action.',
      ]}
      quickActions={[
        { label: 'AR Workspace', to: '/finance/ar' },
        { label: 'Statement of Account', to: '/finance/reports/soa' },
        { label: 'Process Audit', to: '/finance/reports/process-audit' },
        { label: 'Management Accounts', to: '/finance/reports/management', variant: 'secondary' },
      ]}
      headerActions={(
        <>
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting || loading}>
            <Download className={`h-4 w-4 mr-2 ${exporting ? 'animate-pulse' : ''}`} /> Export CSV
          </Button>
        </>
      )}
    >

      <Card>
        <CardContent className="p-4 flex flex-wrap gap-3 items-end">
          <div>
            <p className="text-xs text-muted-foreground mb-1">As Of</p>
            <Input type="date" className="w-40" value={asOf} onChange={e => setAsOf(e.target.value)} />
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Currency View</p>
            <Select value={currencyView} onValueChange={value => setCurrencyView(value as 'functional' | 'presentation')}>
              <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="functional">Functional</SelectItem>
                <SelectItem value="presentation">Presentation</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search customer or ID…" className="pl-9" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <Button onClick={fetchData} disabled={loading}>Run Report</Button>
        </CardContent>
      </Card>

      {/* Bucket totals */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
          {buckets.map(b => (
            <Card key={b.key} className="p-3">
              <div className="text-xs text-muted-foreground">{b.label}</div>
              <div className={`text-sm font-bold mt-1 ${b.color}`}>
                {fmtNumber((data as unknown as Record<string, number>)[`${b.key}_total`] ?? 0)}
              </div>
            </Card>
          ))}
          <Card className="p-3 bg-muted/30">
            <div className="text-xs text-muted-foreground font-medium">Grand Total</div>
            <div className="text-sm font-bold mt-1">{fmtNumber(data.grand_total)}</div>
          </Card>
        </div>
      )}

      <div className="grid gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(320px,1fr)]">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <BrainCircuit className="h-4 w-4 text-primary" />
              AR AI Summary
            </CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3 md:grid-cols-4">
            <div className="rounded-xl border p-3">
              <p className="text-xs text-muted-foreground">DSO</p>
              <p className="mt-1 text-2xl font-semibold">{data?.dso ?? 0}</p>
              <p className="text-xs text-muted-foreground">days</p>
            </div>
            <div className="rounded-xl border p-3">
              <p className="text-xs text-muted-foreground">Open Invoices</p>
              <p className="mt-1 text-2xl font-semibold">{data?.totalInvoiceCount ?? 0}</p>
              <p className="text-xs text-muted-foreground">as of {data?.as_of_date ?? asOf}</p>
            </div>
            <div className="rounded-xl border p-3">
              <p className="text-xs text-muted-foreground">90+ Exposure</p>
              <p className="mt-1 text-2xl font-semibold text-red-700">{fmtNumber(data?.days_over_90_total ?? 0)}</p>
              <p className="text-xs text-muted-foreground">highest-risk bucket</p>
            </div>
            <div className="rounded-xl border p-3">
              <p className="text-xs text-muted-foreground">Top Exposure</p>
              <p className="mt-1 text-sm font-semibold truncate">{topExposure?.customer_name ?? '—'}</p>
              <p className="text-xs text-muted-foreground">{topExposure ? fmtNumber(topExposure.total) : 'No open AR'}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Clock3 className="h-4 w-4 text-primary" />
              Top Overdue Accounts
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {topRiskRows.length === 0 ? (
              <p className="text-sm text-muted-foreground">No 90+ day exposures in the current result set.</p>
            ) : topRiskRows.map((row) => (
              <div key={row.customer_id} className="rounded-xl border p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{row.customer_name}</p>
                    <p className="text-xs text-muted-foreground">{row.customer_id}</p>
                  </div>
                  <Badge variant="outline" className="border-red-200 text-red-700">90+</Badge>
                </div>
                <p className="mt-2 text-sm text-red-700 font-semibold">{fmtNumber(row.days_over_90)}</p>
                <div className="mt-3 flex justify-end">
                  {hasResolvableSoaPartyRef(row.customer_id) ? (
                    <Button asChild size="sm" variant="outline">
                      <Link to={buildSoaRoute({ view: 'ar', partyId: row.customer_id, partyName: row.customer_name, from: statementFrom, to: asOf, autostart: true })}>
                        Open SoA
                      </Link>
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" disabled>
                      SoA unavailable
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <Card>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/30">
                <TableHead className="font-medium whitespace-nowrap">Customer</TableHead>
                <TableHead className="font-medium whitespace-nowrap text-right text-green-700">Current</TableHead>
                <TableHead className="font-medium whitespace-nowrap text-right text-yellow-700">1–30</TableHead>
                <TableHead className="font-medium whitespace-nowrap text-right text-orange-600">31–60</TableHead>
                <TableHead className="font-medium whitespace-nowrap text-right text-red-600">61–90</TableHead>
                <TableHead className="font-medium whitespace-nowrap text-right text-red-800">90+</TableHead>
                <TableHead className="font-medium whitespace-nowrap text-right">Total</TableHead>
                <TableHead className="font-medium whitespace-nowrap text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} className="py-8 text-center text-muted-foreground">
                  <RefreshCw className="h-5 w-5 animate-spin mx-auto mb-2" /> Loading report…
                </TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="py-12 text-center">
                  <AlertCircle className="h-8 w-8 mx-auto mb-2 text-muted-foreground/40" />
                  <p className="text-muted-foreground text-sm">No data available</p>
                </TableCell></TableRow>
              ) : rows.map(r => (
                <TableRow key={r.customer_id} className="hover:bg-muted/20">
                  <TableCell>
                    <div className="font-medium text-sm">{r.customer_name}</div>
                    <div className="text-xs text-muted-foreground">{r.currency}</div>
                  </TableCell>
                  <TableCell className="text-right text-green-700">{fmtNumber(r.current)}</TableCell>
                  <TableCell className="text-right text-yellow-700">{r.days_1_30 > 0 ? fmtNumber(r.days_1_30) : '—'}</TableCell>
                  <TableCell className="text-right text-orange-600">{r.days_31_60 > 0 ? fmtNumber(r.days_31_60) : '—'}</TableCell>
                  <TableCell className="text-right text-red-600">{r.days_61_90 > 0 ? fmtNumber(r.days_61_90) : '—'}</TableCell>
                  <TableCell className="text-right text-red-800 font-medium">{r.days_over_90 > 0 ? fmtNumber(r.days_over_90) : '—'}</TableCell>
                  <TableCell className="text-right font-bold">{fmtNumber(r.total)}</TableCell>
                  <TableCell className="text-right">
                    {hasResolvableSoaPartyRef(r.customer_id) ? (
                      <Button asChild size="sm" variant="outline">
                        <Link to={buildSoaRoute({ view: 'ar', partyId: r.customer_id, partyName: r.customer_name, from: statementFrom, to: asOf, autostart: true })}>
                          SoA
                        </Link>
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" disabled>
                        SoA unavailable
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
      <div className="flex justify-end">
        <Button asChild variant="outline" size="sm">
          <Link to="/finance/ar">
            Open AR workspace
            <ArrowRight className="h-4 w-4" />
          </Link>
        </Button>
      </div>
    </FinanceWorkspaceShell>
  );
}
